import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";
import type {
  EstadoPromesa,
  Promesa,
  PromesaCreateInput,
  PromesaListResponse,
} from "./schemas";

const KEY = "promesas";

export type PromesasFilters = {
  cliente_id?: string;
  estado?: EstadoPromesa | "";
  limit?: number;
  offset?: number;
};

function buildSearchParams(f: PromesasFilters): Record<string, string | number> {
  const p: Record<string, string | number> = {
    limit: f.limit ?? 100,
    offset: f.offset ?? 0,
  };
  if (f.cliente_id) p.cliente_id = f.cliente_id;
  if (f.estado) p.estado = f.estado;
  return p;
}

export function useListPromesas(
  filters: PromesasFilters = {},
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [KEY, filters],
    queryFn: () =>
      apiCall<PromesaListResponse>("get", "/api/v1/cobranza/promesas", {
        searchParams: buildSearchParams(filters),
      }),
    enabled: options.enabled ?? true,
  });
}

export function useCreatePromesa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PromesaCreateInput) =>
      apiCall<Promesa>("post", "/api/v1/cobranza/promesas", {
        json: {
          ...input,
          factura_id: input.factura_id || null,
          motivo: input.motivo || null,
        },
      }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["clientes", p.cliente_id] });
      qc.invalidateQueries({ queryKey: ["contratos"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["facturas"] });
      qc.invalidateQueries({ queryKey: ["pago-rapido"] });
    },
  });
}

export function useCancelPromesa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      apiCall<Promesa>("delete", `/api/v1/cobranza/promesas/${id}`, {
        json: { motivo },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}
